import Select from "react-select";
import { Badge, Col, Form, Row } from "react-bootstrap";
import { Controller, useWatch } from "react-hook-form";
import { DateField, InputField, Label } from "../../../components/custom/programs/fields";
import { GrAddCircle, GrSubtractCircle } from "react-icons/gr";
import { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Button } from "../../../components/core/basic/Button";
import moment from "moment";
import { formatCurrency } from "../../../utils/formatNumber";
import marketPlace from "../../../redux/reducers/marketPlace";
import { css } from "../../../../styled-system/css";
import { Flex } from "../../../../styled-system/jsx";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { Modal } from "../../../components/core/basic/Modal";
import { useTranslation } from "react-i18next";

// Resolves an option label that may be either a plain string or a Pattern B
// selector function (`($) => $["..."]`). Used by SelectField/RadioGroupField
// since constants now expose translatable labels as selectors.
const resolveLabel = (label, t) => (typeof label === "function" ? t(label) : label);

const CheckboxField = ({
  label,
  fieldName,
  control,
  error,
  rules,
  shouldUnregister = false,
  deriveFieldValue,
  onChange = () => {},
  disabled = false,
}) => {
  return (
    <Form.Group>
      <Controller
        name={fieldName}
        control={control}
        rules={rules}
        shouldUnregister={shouldUnregister}
        render={({ field }) => {
          return (
            <Checkbox
              label={label}
              value={deriveFieldValue && typeof deriveFieldValue === "function" ? deriveFieldValue() : field.value}
              isDisabled={disabled}
              onChange={(value) => {
                field.onChange(value);
                onChange?.(value);
              }}
            />
          );
        }}
      />
      {error && error?.message && (
        <span className="text-danger" style={{ marginLeft: "1rem" }}>
          {error?.message}
        </span>
      )}
    </Form.Group>
  );
};

const RadioGroupField = ({
  label,
  fieldName,
  control,
  error,
  rules,
  shouldUnregister = false,
  deriveFieldValue,
  onChange = () => {},
  options = [],
  stacked = false,
  disableDeselection = false,
}) => {
  const { t } = useTranslation(["dsp"]);
  return (
    <Form.Group>
      <Controller
        name={fieldName}
        control={control}
        rules={rules}
        shouldUnregister={shouldUnregister}
        render={({ field }) => {
          return (
            <>
              {label ?
                <Label>{label}</Label>
              : null}
              <Flex gap={stacked ? "none" : "large"} direction={stacked ? "column" : "row"}>
                {options?.map(({ label, value }, i) => {
                  const checked =
                    deriveFieldValue && typeof deriveFieldValue === "function" ?
                      deriveFieldValue(value)
                    : field.value === value;
                  return (
                    <Checkbox
                      key={`${fieldName}-${i}-${value}`}
                      label={resolveLabel(label, t)}
                      value={checked}
                      isDisabled={disableDeselection && checked}
                      onChange={(updatedStatus) => {
                        onChange?.(value, updatedStatus);
                      }}
                    />
                  );
                })}
              </Flex>
            </>
          );
        }}
      />
      {error && error?.message && (
        <span className="text-danger" style={{ marginLeft: "1rem" }}>
          {error?.message}
        </span>
      )}
    </Form.Group>
  );
};

const SelectField = ({
  label,
  toolTip,
  fieldName,
  control,
  options = [],
  disabled,
  error,
  rules,
  shouldUnregister = false,
  errorClassNames = "",
  key = "",
  isClearable = false,
  formatOptionLabel,
}) => {
  const { t } = useTranslation(["dsp"]);
  const resolvedOptions = options?.map((o) => ({ ...o, label: resolveLabel(o.label, t) })) ?? [];
  return (
    <Form.Group>
      {label && <Label toolTip={toolTip}>{label}</Label>}
      <Controller
        name={fieldName}
        control={control}
        rules={rules}
        shouldUnregister={shouldUnregister}
        render={({ field }) => {
          const defaultValue = resolvedOptions?.find((o) => o.value === field.value);
          return (
            <Select
              isSearchable={true}
              name={fieldName}
              isInvalid={error}
              classNamePrefix="select-input"
              options={resolvedOptions}
              onChange={(option) => {
                option ? field.onChange(option.value) : field.onChange(null);
              }}
              defaultValue={defaultValue}
              key={field.value + resolvedOptions.length + key}
              isDisabled={disabled}
              isClearable={isClearable}
              isOptionDisabled={(option) => option.disabled}
              formatOptionLabel={formatOptionLabel}
            />
          );
        }}
      />
      {error && error?.message && (
        <span className={`text-danger ${errorClassNames}`} style={{ marginLeft: "1rem" }}>
          {error?.message}
        </span>
      )}
    </Form.Group>
  );
};

const BudgetCapFields = ({
  fieldName,
  control,
  removeCap,
  appendCap,
  budgetCapCount,
  errors,
  createMode = false,
  lineItem,
  disableBudgetCapFields = false,
}) => {
  const { t } = useTranslation(["dsp"]);
  return (
    <Row className="py-3">
      <Col xs={12} lg={createMode ? 4 : 2} className="pe-4">
        <SelectField
          label={t(($) => $.dsp["Period"])}
          options={[
            { label: ($) => $.dsp["Daily"], value: "DAILY" },
            { label: ($) => $.dsp["Monthly"], value: "MONTHLY" },
          ]}
          fieldName={`${fieldName}.recurrenceTimePeriod`}
          control={control}
          disabled={budgetCapCount === 2 || disableBudgetCapFields}
        />
      </Col>
      <Col xs={12} lg={createMode || lineItem ? 4 : 2} className="ps-4 d-flex">
        <InputField
          label={t(($) => $.dsp["Spending Limit"])}
          fieldName={`${fieldName}.amount`}
          control={control}
          errors={errors}
          type="number"
          rules={{
            min: {
              value: 1,
              message: t(($) => $.dsp["Spending limit must be at least {min}"], {
                min: formatCurrency(1, {
                  marketPlace: marketPlace.marketPlace,
                  decimalPlaces: 0,
                }),
              }),
            },
            required: {
              value: true,
              message: t(($) => $.dsp["Spending limit is required"]),
            },
          }}
          errorClassNames={createMode ? "d-block ms-2 mt-2 fs-5" : "d-block ms-2 mt-2"}
          disabled={disableBudgetCapFields}
        />
      </Col>
      {!disableBudgetCapFields && (
        <Col xs={12} lg={1} className="mt-auto pb-4 d-flex gap-3">
          <GrSubtractCircle className="fs-4 text-muted" onClick={() => removeCap()} />
          {budgetCapCount < 2 && <GrAddCircle className="fs-4 text-muted" onClick={() => appendCap()} />}
        </Col>
      )}
    </Row>
  );
};

const FlightFields = ({
  fieldName,
  control,
  removeFlight,
  errors,
  createMode,
  orderFlights,
  index,
  disableFlightFields,
}) => {
  const { t } = useTranslation(["dsp"]);
  const flightDetails = useWatch({ control, name: fieldName });

  if (!flightDetails) {
    return null;
  }

  const minDate =
    index ?
      moment(orderFlights[index - 1]?.endDateTime)
        .add(1, "day")
        .format("YYYY-MM-DD")
    : null;
  const maxDate =
    index < orderFlights?.length - 1 ? moment(orderFlights[index + 1]?.startDateTime).format("YYYY-MM-DD") : null;

  return (
    <Row className="py-3">
      <Col xs={12} lg={createMode ? 3 : 2}>
        <DateField
          label={t(($) => $.dsp["Start Date"])}
          disabled={
            (moment(flightDetails.startDateTime).isBefore(moment()) && flightDetails.flightId) || disableFlightFields
          }
          fieldName={`${fieldName}.startDateTime`}
          control={control}
          errors={errors}
          rules={{
            required: { value: true, message: t(($) => $.dsp["Start date is required"]) },
          }}
          errorClassNames="d-block ms-2 mt-0"
          disableKeyboardInput={true}
          minDate={minDate}
          showTimeInput={true}
        />
      </Col>
      <Col xs={12} lg={createMode ? 3 : 2}>
        <DateField
          label={t(($) => $.dsp["End Date"])}
          disabled={
            (moment(flightDetails.endDateTime).isBefore(moment()) && flightDetails.flightId) || disableFlightFields
          }
          fieldName={`${fieldName}.endDateTime`}
          control={control}
          errors={errors}
          rules={{
            required: { value: true, message: t(($) => $.dsp["An end date is required"]) },
          }}
          errorClassNames="d-block ms-2 mt-0"
          disableKeyboardInput={true}
          maxDate={maxDate}
          showTimeInput={true}
        />
      </Col>
      <Col xs={12} lg={createMode ? 3 : 2} className="ps-4 d-flex">
        <InputField
          label={t(($) => $.dsp["Budget"])}
          fieldName={`${fieldName}.budgetAmount`}
          control={control}
          errors={errors}
          type="number"
          rules={{
            min: {
              value: 1,
              message: t(($) => $.dsp["Budget must be at least {min}"], {
                min: formatCurrency(1, {
                  marketPlace: marketPlace.marketPlace,
                  decimalPlaces: 0,
                }),
              }),
            },
            required: { value: true, message: t(($) => $.dsp["Budget is required"]) },
          }}
          errorClassNames={createMode ? "d-block ms-2 mt-2 fs-5" : "d-block ms-2 mt-0"}
          disabled={
            (moment(flightDetails.startDateTime).isBefore(moment()) &&
              flightDetails.flightId &&
              moment(flightDetails.endDateTime).isBefore(moment())) ||
            disableFlightFields
          }
        />
      </Col>
      {!moment(flightDetails.startDateTime).isBefore(moment()) && (
        <Col xs={12} lg={1} className="mt-auto pb-4 d-flex gap-3">
          <GrSubtractCircle className="fs-4 text-muted" onClick={() => removeFlight()} />
        </Col>
      )}
    </Row>
  );
};

const ProductCategoriesField = ({
  fieldName,
  lineItemCategories,
  onRemoveCategory,
  onAddCategory,
  onResetCategorySelections,
  errors,
  categoryOptions = [],
}) => {
  const { t } = useTranslation(["dsp"]);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [search, setSearch] = useState("");

  const parentCategories = categoryOptions?.filter((category) => !category.parentId);
  const childCategories = categoryOptions?.filter((category) => category.parentId);

  childCategories?.forEach((child) => {
    const parent = parentCategories.find((p) => p.id === child.parentId);
    parent.children += child.name;
  });

  const selectedLineItemCategories = categoryOptions?.filter((category) => lineItemCategories?.includes(category.id));

  return (
    <Form.Group>
      <Label>{t(($) => $.dsp["Product Categories"])}</Label>
      <p>{t(($) => $.dsp["{count} Selected Product Categories"], { count: selectedLineItemCategories.length })}</p>
      {selectedLineItemCategories?.map((category) => (
        <Checkbox
          key={`${fieldName}-${category.id}`}
          value={true}
          label={category.name}
          onChange={() => {
            onRemoveCategory(category.id);
          }}
        />
      ))}
      {errors?.advProductCategoryIds && (
        <span
          className={`text-danger `}
          style={{ marginTop: ".5rem", position: "absolute", textWrap: "nowrap", fontSize: "10px" }}
        >
          {errors?.advProductCategoryIds?.message}
        </span>
      )}
      <Row className="py-3">
        <Button
          onPress={() => {
            if (showCategorySelector) {
              setSearch("");
              setSelectedParentCategory(null);
            }

            setShowCategorySelector(!showCategorySelector);
          }}
          className={css({ transform: "scale(.85)", marginTop: "10px" })}
        >
          {showCategorySelector ? t(($) => $.dsp["Cancel"]) : t(($) => $.dsp["Select New Categories"])}
        </Button>

        <Modal
          isOpen={showCategorySelector}
          onOpenChange={(isOpen) => setShowCategorySelector(isOpen)}
          title={t(($) => $.dsp["Update Product Categories"])}
          css={{
            width: "70vw",
            maxHeight: "98vh",
            paddingRight: "xlarge",
            overflow: "scroll",
          }}
        >
          <Row>
            <Col xs={12} lg={{ span: 8, offset: 2 }}>
              <Form.Control
                placeholder={t(($) => $.dsp["Search..."])}
                aria-label={t(($) => $.dsp["Search Term"])}
                onChange={(e) => {
                  setSelectedParentCategory(null);
                  setSearch(e.target.value);
                }}
                value={search}
                style={{ width: "90%" }}
                className="mt-3 mb-5  mx-auto"
              />
            </Col>
          </Row>
          <Row className="py-3 px-5" style={{ margin: "0 50px" }}>
            <Col xs={12} lg={8}>
              <h4 className="fs-4 my-0 fw-bold">{t(($) => $.dsp["Category Options"])}</h4>
              <div
                style={{ overflowY: "auto", height: "500px" }}
                className="pe-5 custom-scroll-container category-target-search-scroll-container"
              >
                {parentCategories
                  ?.filter((parentCategory) => {
                    if (search) {
                      return (
                        parentCategory.name.toLowerCase().includes(search.toLowerCase()) ||
                        parentCategory.children.toLowerCase().includes(search.toLowerCase())
                      );
                    }
                    return true;
                  })
                  ?.map((parentCategory) => (
                    <Row className="text-muted" key={parentCategory.id}>
                      <div
                        onClick={() => {
                          selectedParentCategory === parentCategory.id ?
                            setSelectedParentCategory(null)
                          : setSelectedParentCategory(parentCategory.id);
                          setSearch("");
                        }}
                        className="py-1 d-flex align-items-center justify-content-between"
                        style={{ cursor: "pointer" }}
                      >
                        {parentCategory.name}{" "}
                        {selectedParentCategory === parentCategory.id ?
                          <FaChevronUp className="fs-5" />
                        : <FaChevronDown className="fs-5" />}
                      </div>
                      {(selectedParentCategory === parentCategory.id ||
                        (search && parentCategory.children.toLowerCase().includes(search.toLowerCase()))) && (
                        <Col xs={12}>
                          {categoryOptions
                            ?.filter((category) => {
                              if (selectedParentCategory) {
                                return category.parentId === selectedParentCategory;
                              }
                              if (search) {
                                return (
                                  category.name.toLowerCase().includes(search.toLowerCase()) &&
                                  category.parentId === parentCategory.id
                                );
                              }
                              return false;
                            })
                            ?.map((category) => (
                              <Checkbox
                                key={`${fieldName}-${category.id}`}
                                value={lineItemCategories?.includes(category.id)}
                                label={category.name}
                                onChange={() => {
                                  if (lineItemCategories?.includes(category.id)) {
                                    onRemoveCategory(category.id);
                                  } else {
                                    onAddCategory(category.id);
                                  }
                                }}
                              />
                            ))}
                        </Col>
                      )}
                    </Row>
                  ))}
              </div>
            </Col>
            <Col xs={12} lg={{ span: 4 }} className="border-start px-3">
              <h4 className="px-4 fs-4 my-0 fw-bold d-flex align-items-center gap-2">
                {t(($) => $.dsp["Selected Categories"])}{" "}
                <Badge className={`badge badge-secondary`} pill>
                  {selectedLineItemCategories?.length}
                </Badge>
              </h4>
              {selectedLineItemCategories?.map((category) => (
                <Checkbox
                  key={`${fieldName}-${category.id}`}
                  value={true}
                  label={category.name}
                  onChange={() => {
                    onRemoveCategory(category.id);
                  }}
                />
              ))}
            </Col>
          </Row>
          <Row className="d-flex align-items-center justify-content-center gap-5 py-4 fs-5">
            <Button
              variant="secondary"
              onPress={() => {
                onResetCategorySelections();
                setShowCategorySelector(false);
                setSearch("");
                setSelectedParentCategory(null);
              }}
            >
              {t(($) => $.dsp["Cancel"])}
            </Button>
            <Button
              onPress={() => {
                setShowCategorySelector(false);
              }}
            >
              {t(($) => $.dsp["Confirm"])}
            </Button>
          </Row>
        </Modal>
      </Row>
    </Form.Group>
  );
};

export { CheckboxField, SelectField, RadioGroupField, FlightFields, BudgetCapFields, ProductCategoriesField };
